/**
 * Frontend error capture and log forwarding.
 *
 * Captures unhandled errors, unhandled promise rejections, and provides
 * a manual logging API. Batches log entries and POSTs them to the backend
 * `/api/logs` endpoint for centralized ingestion into CloudWatch.
 *
 * - Error-level entries flush immediately
 * - Info/warn entries buffer for up to 5 seconds
 * - Uses `navigator.sendBeacon` on `beforeunload` to flush remaining entries
 * - Captures `x-request-id` from API responses for correlation
 *
 * Zero dependencies — uses native browser APIs only.
 *
 * @example
 *   import { frontendLogger, initGlobalErrorHandlers } from "../lib/frontendLogger";
 *
 *   // In App mount:
 *   initGlobalErrorHandlers();
 *
 *   // Manual logging:
 *   frontendLogger.error("checkout.payment_failed", { orderId: "abc" });
 *   frontendLogger.info("onboarding.step_completed", { step: 2 });
 */

export type FrontendLogLevel = "error" | "warn" | "info" | "debug";

interface FrontendLogEntry {
  timestamp: string;
  level: FrontendLogLevel;
  event: string;
  source: "frontend";
  url: string;
  userAgent: string;
  [key: string]: unknown;
}

const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:4000";

/** How long to buffer non-error entries before flushing (ms). */
const FLUSH_INTERVAL_MS = 5_000;

/** Maximum entries to buffer before forcing a flush. */
const MAX_BUFFER_SIZE = 50;

let buffer: FrontendLogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Build a log entry with standard fields.
 */
const createEntry = (
  level: FrontendLogLevel,
  event: string,
  context?: Record<string, unknown>
): FrontendLogEntry => ({
  timestamp: new Date().toISOString(),
  level,
  event,
  source: "frontend",
  url: window.location.href,
  userAgent: navigator.userAgent,
  ...context,
});

/**
 * Send buffered entries to the backend. Falls back to `navigator.sendBeacon`
 * if `fetch` is unavailable (e.g. during page unload).
 */
const sendEntries = (entries: FrontendLogEntry[], useBeacon = false): void => {
  if (entries.length === 0) return;

  const payload = JSON.stringify({ entries });
  const url = `${apiBaseUrl}/api/logs`;

  if (useBeacon && typeof navigator.sendBeacon === "function") {
    const blob = new Blob([payload], { type: "application/json" });
    navigator.sendBeacon(url, blob);
    return;
  }

  // Fire-and-forget — we don't want logging failures to affect the app
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    credentials: "include",
    keepalive: true,
  }).catch(() => {
    // Silently drop — logging infrastructure should not throw
  });
};

/**
 * Flush all buffered entries to the backend.
 */
const flush = (useBeacon = false): void => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (buffer.length === 0) return;

  const entries = buffer;
  buffer = [];
  sendEntries(entries, useBeacon);
};

/**
 * Schedule a flush if one isn't already pending.
 */
const scheduleFlush = (): void => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flush();
  }, FLUSH_INTERVAL_MS);
};

/**
 * Add an entry to the buffer. Errors flush immediately;
 * other levels buffer and flush on a timer.
 */
const log = (level: FrontendLogLevel, event: string, context?: Record<string, unknown>): void => {
  const entry = createEntry(level, event, context);
  buffer.push(entry);

  if (level === "error" || buffer.length >= MAX_BUFFER_SIZE) {
    flush();
  } else {
    scheduleFlush();
  }
};

/**
 * The frontend logger instance.
 */
export const frontendLogger = {
  error(event: string, context?: Record<string, unknown>): void {
    log("error", event, context);
  },
  warn(event: string, context?: Record<string, unknown>): void {
    log("warn", event, context);
  },
  info(event: string, context?: Record<string, unknown>): void {
    log("info", event, context);
  },
  debug(event: string, context?: Record<string, unknown>): void {
    log("debug", event, context);
  },
};

/**
 * Install global error handlers to capture unhandled errors and
 * unhandled promise rejections. Call once at app startup.
 */
export const initGlobalErrorHandlers = (): void => {
  // Unhandled JS errors
  window.addEventListener("error", (event: ErrorEvent) => {
    const err: unknown = event.error;
    frontendLogger.error("global.unhandled_error", {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: err instanceof Error ? err.stack : undefined,
    });
  });

  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason: unknown = event.reason;
    frontendLogger.error("global.unhandled_rejection", {
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });

  // Flush remaining entries on page unload
  window.addEventListener("beforeunload", () => {
    flush(true); // Use sendBeacon for reliability
  });
};

/**
 * Extract `x-request-id` from a fetch Response for log correlation.
 * Call this after API responses to attach the backend request ID.
 */
export const getRequestIdFromResponse = (response: Response): string | null => {
  return response.headers.get("x-request-id");
};
